import type { AppRuntime, AppSnapshot, ManagerVm } from './app-runtime.js';
import type {
    UiBridgeV2,
    UiCommandBusV2,
    UiCommandResult,
    UiFixtureV2,
    UiInboxItemV2,
    UiTransferListedPlayerV2,
    UiTransferIncomingOfferV2,
    UiManagerMainRoute,
    UiManagerOverlayRoute,
    UiSnapshotV2,
    UiSquadPlayerV2,
    UiTransferNegotiationV2,
    UiTransferStatus,
    UiTransferTargetV2
} from './contracts/manager-ui-v2.js';

const MAIN_ROUTES: UiManagerMainRoute[] = [
    '/manager/home',
    '/manager/competitions',
    '/manager/inbox',
    '/manager/squad',
    '/manager/fixtures',
    '/manager/staff',
    '/manager/transfers'
];

const MAIN_ROUTE_BY_SCREEN: Record<'home' | 'inbox' | 'squad' | 'fixtures' | 'staff' | 'transfers', UiManagerMainRoute> = {
    home: '/manager/home',
    inbox: '/manager/inbox',
    squad: '/manager/squad',
    fixtures: '/manager/fixtures',
    staff: '/manager/staff',
    transfers: '/manager/transfers'
};

function resolveMainRoute(pathname: string): UiManagerMainRoute {
    const route = MAIN_ROUTES.find((entry) => entry === pathname);
    return route ?? '/manager/home';
}

function resolveOverlayRoute(pathname: string, manager: ManagerVm | null): UiManagerOverlayRoute | null {
    if (pathname === '/manager/intro') {
        return '/manager/intro';
    }
    if (pathname === '/manager/result') {
        return '/manager/result';
    }
    if (pathname === '/manager/day-summary') {
        return '/manager/day-summary';
    }
    if (manager?.intro) {
        return '/manager/intro';
    }
    if (manager?.result) {
        return '/manager/result';
    }
    if (manager?.daySummary) {
        return '/manager/day-summary';
    }
    return null;
}

function formatFriendlyDate(value: string): string {
    const raw = String(value ?? '').trim();
    if (!raw) {
        return '--';
    }
    const lower = raw.toLowerCase();
    if (lower === 'today' || lower === 'yesterday' || lower === 'tomorrow') {
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    }
    const safe = raw.length <= 10 ? `${raw}T12:00:00.000Z` : raw;
    const parsed = new Date(safe);
    if (Number.isNaN(parsed.getTime())) {
        return raw;
    }
    const showTime = raw.length > 10 && /T\d{2}:\d{2}/.test(raw);
    return new Intl.DateTimeFormat(
        undefined,
        showTime
            ? { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
            : { year: 'numeric', month: 'short', day: 'numeric' }
    ).format(parsed);
}

function mapInboxItems(manager: ManagerVm | null): UiInboxItemV2[] {
    return (manager?.inbox ?? []).map((item) => ({
        id: item.id,
        sender: item.sender,
        subject: item.subject,
        preview: item.preview,
        prompt: item.prompt,
        date: formatFriendlyDate(item.date),
        status: item.status,
        read: item.read,
        hasActions: item.hasActions,
        options: item.options,
        transferFocusTargetId: item.transferFocusTargetId ?? null,
        transferThreadStage: item.transferThreadStage ?? null,
        transferThreadVersion: item.transferThreadVersion ?? null,
        transferPlayerSummary: item.transferPlayerSummary ?? null,
        transferScoutReport: item.transferScoutReport ?? null,
        transferMarketEvent: item.transferMarketEvent ?? null,
        contextSections: item.contextSections ?? [],
        focusPlayerContext: item.focusPlayerContext ?? null,
        avatarSvg: item.avatar
    }));
}

function mapSquadPlayers(manager: ManagerVm | null): UiSquadPlayerV2[] {
    return (manager?.squad.players ?? []).map((player) => ({
        id: player.id,
        name: player.name,
        role: player.role,
        readiness: player.readiness,
        availability: player.availability ?? 'available',
        injuryStatus: player.injuryStatus,
        suspensionStatus: player.suspensionStatus,
        rating: player.rating,
        morale: player.morale,
        avatarSvg: player.avatar,
        listedForTransfer: player.listedForTransfer,
        listedTargetPrice: player.listedTargetPrice ?? null,
        keySkills: player.keySkills,
        attributes: {
            pace: Math.round(Number(player.attributes?.pace ?? 50)),
            ballControl: Math.round(Number(player.attributes?.ballControl ?? 50)),
            passing: Math.round(Number(player.attributes?.passing ?? 50)),
            tackling: Math.round(Number(player.attributes?.tackling ?? 50)),
            endurance: Math.round(Number(player.attributes?.endurance ?? 50)),
            morale: Math.round(Number(player.attributes?.morale ?? 50)),
            positioning: Math.round(Number(player.attributes?.positioning ?? 50)),
            shotPower: Math.round(Number(player.attributes?.shotPower ?? 50)),
            jumping: Math.round(Number(player.attributes?.jumping ?? 50)),
            reactions: Math.round(Number(player.attributes?.reactions ?? 50)),
            composure: Math.round(Number(player.attributes?.composure ?? 50)),
            goalkeeping: Math.round(Number(player.attributes?.goalkeeping ?? (player.role === 'GK' ? 68 : 30)))
        }
    }));
}

function mapTransferStatus(status: string): UiTransferStatus {
    if (status === 'available') {
        return 'scouted';
    }
    if (status === 'offered') {
        return 'bid_prepared';
    }
    if (status === 'negotiating') {
        return 'awaiting_club_response';
    }
    const allowed: UiTransferStatus[] = [
        'scouted',
        'scouting_in_progress',
        'cooldown',
        'shortlisted',
        'bid_prepared',
        'awaiting_club_response',
        'club_rejected',
        'club_accepted',
        'awaiting_player_response',
        'personal_terms_failed',
        'deal_agreed',
        'signed',
        'offer_received',
        'offer_countered',
        'withdrawn',
        'expired'
    ];
    if (allowed.includes(status as UiTransferStatus)) {
        return status as UiTransferStatus;
    }
    return 'scouted';
}

function mapTransferTargets(targets: Array<{
    id: string;
    name: string;
    position: string;
    rating: number;
    value: number;
    status: string;
    clubName?: string;
    listingStatus?: 'not_listed' | 'club_listed' | 'player_requested' | 'market_listed';
    listedBy?: 'club' | 'player';
    listingReason?: 'club_clearout' | 'surplus_to_requirements' | 'financial_pressure' | 'player_requested_move' | 'contract_running_down' | 'strategic_sale';
    wageExpectation?: number;
    avatar?: string;
    scoutConfidence?: number;
    attributes?: {
        pace?: number;
        ballControl?: number;
        passing?: number;
        tackling?: number;
        endurance?: number;
        morale?: number;
        positioning?: number;
        shotPower?: number;
        jumping?: number;
        reactions?: number;
        composure?: number;
        goalkeeping?: number;
    };
    scoutUncertaintyByAttribute?: {
        pace?: number;
        ballControl?: number;
        passing?: number;
        tackling?: number;
        endurance?: number;
        morale?: number;
        positioning?: number;
        shotPower?: number;
        jumping?: number;
        reactions?: number;
        composure?: number;
        goalkeeping?: number;
    };
}>): UiTransferTargetV2[] {
    return targets.map((target) => ({
        id: target.id,
        name: target.name,
        position: target.position,
        rating: target.rating,
        value: target.value,
        status: mapTransferStatus(target.status),
        clubName: target.clubName,
        listingStatus: target.listingStatus,
        listedBy: target.listedBy,
        listingReason: target.listingReason,
        wageExpectation: target.wageExpectation,
        cooldownDaysRemaining: Number.isFinite((target as { cooldownDaysRemaining?: number }).cooldownDaysRemaining)
            ? Math.max(0, Math.round(Number((target as { cooldownDaysRemaining?: number }).cooldownDaysRemaining)))
            : undefined,
        avatarSvg: target.avatar,
        scoutConfidence: Number.isFinite(target.scoutConfidence) ? Math.round(Number(target.scoutConfidence)) : undefined,
        attributes: target.attributes ? {
            pace: Math.round(Number(target.attributes.pace ?? 50)),
            ballControl: Math.round(Number(target.attributes.ballControl ?? 50)),
            passing: Math.round(Number(target.attributes.passing ?? 50)),
            tackling: Math.round(Number(target.attributes.tackling ?? 50)),
            endurance: Math.round(Number(target.attributes.endurance ?? 50)),
            morale: Math.round(Number(target.attributes.morale ?? 50)),
            positioning: Math.round(Number(target.attributes.positioning ?? 50)),
            shotPower: Math.round(Number(target.attributes.shotPower ?? 50)),
            jumping: Math.round(Number(target.attributes.jumping ?? 50)),
            reactions: Math.round(Number(target.attributes.reactions ?? 50)),
            composure: Math.round(Number(target.attributes.composure ?? 50)),
            goalkeeping: Math.round(Number(target.attributes.goalkeeping ?? 30))
        } : undefined,
        scoutUncertaintyByAttribute: target.scoutUncertaintyByAttribute ? {
            pace: Math.round(Number(target.scoutUncertaintyByAttribute.pace ?? 0)),
            ballControl: Math.round(Number(target.scoutUncertaintyByAttribute.ballControl ?? 0)),
            passing: Math.round(Number(target.scoutUncertaintyByAttribute.passing ?? 0)),
            tackling: Math.round(Number(target.scoutUncertaintyByAttribute.tackling ?? 0)),
            endurance: Math.round(Number(target.scoutUncertaintyByAttribute.endurance ?? 0)),
            morale: Math.round(Number(target.scoutUncertaintyByAttribute.morale ?? 0)),
            positioning: Math.round(Number(target.scoutUncertaintyByAttribute.positioning ?? 0)),
            shotPower: Math.round(Number(target.scoutUncertaintyByAttribute.shotPower ?? 0)),
            jumping: Math.round(Number(target.scoutUncertaintyByAttribute.jumping ?? 0)),
            reactions: Math.round(Number(target.scoutUncertaintyByAttribute.reactions ?? 0)),
            composure: Math.round(Number(target.scoutUncertaintyByAttribute.composure ?? 0)),
            goalkeeping: Math.round(Number(target.scoutUncertaintyByAttribute.goalkeeping ?? 0))
        } : undefined
    }));
}

function mapTransferListedPlayers(players: ManagerVm['transfers']['listedPlayers']): UiTransferListedPlayerV2[] {
    return players.map((player) => ({
        playerId: player.playerId,
        name: player.name,
        position: player.position,
        rating: player.rating,
        value: player.value,
        listedTargetPrice: player.listedTargetPrice,
        avatarSvg: player.avatar,
        attributes: {
            pace: Math.round(Number(player.attributes.pace ?? 50)),
            ballControl: Math.round(Number(player.attributes.ballControl ?? 50)),
            passing: Math.round(Number(player.attributes.passing ?? 50)),
            tackling: Math.round(Number(player.attributes.tackling ?? 50)),
            endurance: Math.round(Number(player.attributes.endurance ?? 50)),
            morale: Math.round(Number(player.attributes.morale ?? 50)),
            positioning: Math.round(Number(player.attributes.positioning ?? 50)),
            shotPower: Math.round(Number(player.attributes.shotPower ?? 50)),
            jumping: Math.round(Number(player.attributes.jumping ?? 50)),
            reactions: Math.round(Number(player.attributes.reactions ?? 50)),
            composure: Math.round(Number(player.attributes.composure ?? 50)),
            goalkeeping: Math.round(Number(player.attributes.goalkeeping ?? 30))
        }
    }));
}

function mapIncomingOffers(offers: ManagerVm['transfers']['incomingOffers']): UiTransferIncomingOfferV2[] {
    return offers.map((offer) => ({
        id: offer.id,
        playerId: offer.playerId,
        playerName: offer.playerName,
        role: offer.role,
        offeringClubName: offer.offeringClubName,
        offeredFee: offer.offeredFee,
        wageOffer: offer.wageOffer,
        requestedFee: offer.requestedFee,
        status: offer.status,
        responseMessage: offer.responseMessage,
        daysRemaining: offer.daysRemaining
    }));
}

function mapNegotiations(negotiations: ManagerVm['transfers']['negotiations']): UiTransferNegotiationV2[] {
    return negotiations.map((negotiation) => ({
        id: negotiation.id,
        targetId: negotiation.targetId,
        playerName: negotiation.playerName,
        role: negotiation.role,
        counterpartyClubName: negotiation.counterpartyClubName,
        status: negotiation.status,
        bidAmount: negotiation.bidAmount,
        wageOffer: negotiation.wageOffer,
        requestedFee: negotiation.requestedFee,
        responseMessage: negotiation.responseMessage,
        daysRemaining: negotiation.daysRemaining
    }));
}

function mapFixtures(manager: ManagerVm | null): UiFixtureV2[] {
    return (manager?.fixtures ?? []).map((fixture) => ({
        matchday: fixture.matchday,
        date: fixture.date,
        home: fixture.home,
        away: fixture.away,
        homeScore: fixture.homeScore,
        awayScore: fixture.awayScore,
        status: fixture.status
    }));
}

export function mapUiSnapshotV2(snapshot: AppSnapshot, pathname: string): UiSnapshotV2 {
    const manager = snapshot.manager;
    const mainRoute = resolveMainRoute(pathname);
    const overlayRoute = resolveOverlayRoute(pathname, manager);
    const inboxBadgeCount = manager?.layout.inboxBadgeCount ?? 0;

    return {
        version: 2,
        ready: Boolean(manager),
        teamName: manager?.teamName ?? 'Managed United',
        managerName: manager?.managerName ?? 'Manager',
        activeRoute: mainRoute,
        activeOverlayRoute: overlayRoute,
        navItems: [
            { id: 'home', label: 'Home', route: '/manager/home' },
            { id: 'inbox', label: 'Inbox', route: '/manager/inbox', badgeCount: inboxBadgeCount },
            { id: 'competitions', label: 'Standings', route: '/manager/competitions' },
            { id: 'squad', label: 'Squad', route: '/manager/squad' },
            { id: 'fixtures', label: 'Fixtures', route: '/manager/fixtures' },
            { id: 'staff', label: 'Club', route: '/manager/staff' },
            { id: 'transfers', label: 'Transfers', route: '/manager/transfers' }
        ],
        routes: {
            home: {
                leagueTable: manager?.home.leagueTable ?? [],
                summary: {
                    dateLabel: manager?.layout.dateLabel ?? 'Date: --',
                    nextLabel: manager?.layout.nextLabel ?? 'Next: --',
                    financeLabel: manager?.layout.financeLabel ?? 'Budget: --',
                    wagesPerWeek: Number(manager?.layout.wagesPerWeek ?? 0),
                    ctaLabel: manager?.layout.ctaLabel ?? 'Action: --',
                    message: manager?.layout.message ?? ''
                },
                inboxBadgeCount
            },
            competitions: {
                leagueTable: manager?.competitions?.leagueTable ?? manager?.home.fullLeagueTable ?? manager?.home.leagueTable ?? []
            },
            inbox: {
                items: mapInboxItems(manager)
            },
            squad: {
                players: mapSquadPlayers(manager),
                selectedPlayerId: manager?.squad.selectedPlayerId ?? null
            },
            fixtures: {
                fixtures: mapFixtures(manager)
            },
            club: {
                staff: manager?.staff ?? [],
                financeLabel: manager?.layout.financeLabel ?? 'Budget: --'
            },
            transfers: {
                targets: mapTransferTargets(manager?.transfers.targets ?? []),
                shortlist: mapTransferTargets(manager?.transfers.shortlist ?? []),
                listedPlayers: mapTransferListedPlayers(manager?.transfers.listedPlayers ?? []),
                focusTargetId: manager?.transfers.focusTargetId ?? null,
                focusVersion: manager?.transfers.focusVersion ?? 0,
                incomingOffers: mapIncomingOffers(manager?.transfers.incomingOffers ?? []),
                negotiations: mapNegotiations(manager?.transfers.negotiations ?? [])
            },
            intro: {
                visible: Boolean(manager?.intro),
                fixture: manager?.intro?.fixture ?? manager?.teamSelectionContext?.fixture ?? '',
                date: formatFriendlyDate(manager?.intro?.date ?? manager?.teamSelectionContext?.date ?? ''),
                location: manager?.intro?.location ?? manager?.teamSelectionContext?.location ?? '',
                competition: manager?.intro?.competition ?? manager?.teamSelectionContext?.competition ?? '',
                validity: manager?.intro?.validity ?? '',
                selection: {
                    formationName: manager?.intro?.selection?.formationName ?? manager?.teamSelection?.formationName ?? '4-4-2',
                    formationOptions: manager?.intro?.selection?.formationOptions ?? manager?.teamSelection?.formationOptions ?? ['4-4-2'],
                    validation: {
                        ok: manager?.intro?.selection?.validation?.ok ?? manager?.teamSelection?.validation?.ok ?? false,
                        errors: manager?.intro?.selection?.validation?.errors ?? manager?.teamSelection?.validation?.errors ?? []
                    },
                    xiSlots: (manager?.intro?.selection?.xiSlots ?? manager?.teamSelection?.xiSlots ?? []).map((slot) => ({ ...slot, avatarSvg: slot.avatar })),
                    benchSlots: (manager?.intro?.selection?.benchSlots ?? manager?.teamSelection?.benchSlots ?? []).map((slot) => ({ ...slot, avatarSvg: slot.avatar })),
                    outSlots: (manager?.intro?.selection?.outSlots ?? manager?.teamSelection?.outSlots ?? []).map((slot) => ({ ...slot, avatarSvg: slot.avatar }))
                }
            },
            result: {
                visible: Boolean(manager?.result),
                score: manager?.result?.score ?? '',
                fixture: manager?.result?.fixture ?? '',
                scorers: manager?.result?.scorers ?? [],
                otherResults: manager?.result?.otherResults ?? []
            },
            daySummary: {
                visible: Boolean(manager?.daySummary),
                headline: manager?.daySummary?.headline ?? '',
                date: formatFriendlyDate(manager?.daySummary?.date ?? ''),
                progress: manager?.daySummary?.progress ?? '',
                highlights: manager?.daySummary?.highlights ?? [],
                fixtures: manager?.daySummary?.fixtures ?? []
            }
        }
    };
}

function ok<TPayload = undefined>(reason: string, payload?: TPayload): UiCommandResult<TPayload> {
    return { ok: true, reason, payload };
}

function failure(reason: string): UiCommandResult {
    return { ok: false, reason };
}

async function executeCommand(
    action: () => Promise<void>,
    successReason: string
): Promise<UiCommandResult> {
    try {
        await action();
        return ok(successReason);
    } catch (error) {
        const reason = error instanceof Error && error.message ? error.message : 'Command failed.';
        return failure(reason);
    }
}

export function createUiCommandBusV2(
    runtime: AppRuntime,
    navigate: (path: string) => void
): UiCommandBusV2 {
    return {
        async navigate(route) {
            navigate(route);
            return ok('', { route });
        },
        async setManagerScreen(screen) {
            return executeCommand(() => runtime.managerSetScreen(screen), 'Manager screen updated.');
        },
        async nextDay() {
            return executeCommand(() => runtime.managerNextDay(), 'Advanced manager day.');
        },
        async inboxOpen(messageId) {
            const result = await executeCommand(() => runtime.managerOpenMessage(messageId), 'Inbox message opened.');
            return { ...result, payload: result.ok ? { messageId } : undefined };
        },
        async inboxResolve(itemId, optionId, payload) {
            const actionPayload: Record<string, string> = {
                inboxResolve: itemId,
                inboxOption: optionId
            };
            if (Number.isFinite(payload?.bidAmount)) {
                actionPayload.bidAmount = String(payload?.bidAmount);
            }
            if (Number.isFinite(payload?.wageOffer)) {
                actionPayload.wageOffer = String(payload?.wageOffer);
            }
            if (Number.isFinite(payload?.listingPrice)) {
                actionPayload.listingPrice = String(payload?.listingPrice);
            }
            const result = await executeCommand(
                () => runtime.managerInboxAction('resolve', actionPayload),
                'Inbox item resolved.'
            );
            return { ...result, payload: result.ok ? { itemId, optionId } : undefined };
        },
        async squadSelectPlayer(playerId) {
            const result = await executeCommand(() => runtime.managerSelectPlayer(playerId), 'Squad player selected.');
            return { ...result, payload: result.ok ? { playerId } : undefined };
        },
        async squadSetTrainingPlan(playerId, plan) {
            return executeCommand(
                () => runtime.managerSquadAction('set-training-plan', { playerId: String(playerId), value: plan }),
                'Training plan updated.'
            );
        },
        async squadSetTrainingIntensity(playerId, intensity) {
            return executeCommand(
                () => runtime.managerSquadAction('set-training-intensity', { playerId: String(playerId), value: String(intensity) }),
                'Training intensity updated.'
            );
        },
        async squadListPlayer(playerId, targetPrice) {
            return executeCommand(
                () => runtime.managerSquadAction('transfer-list', { playerId: String(playerId), targetPrice: String(targetPrice) }),
                'Player listed for transfer.'
            );
        },
        async squadUnlistPlayer(playerId) {
            return executeCommand(
                () => runtime.managerSquadAction('transfer-unlist', { playerId: String(playerId) }),
                'Player removed from transfer list.'
            );
        },
        async staffHire(role) {
            return executeCommand(
                () => runtime.managerStaffAction('hire', { role }),
                'Staff hire action submitted.'
            );
        },
        async staffFire(staffId) {
            return executeCommand(
                () => runtime.managerStaffAction('fire', { staffFire: staffId }),
                'Staff fire action submitted.'
            );
        },
        async transferFocusTarget(targetId) {
            const result = await executeCommand(
                () => runtime.managerTransfersAction('action', { transferFocusTarget: targetId }),
                'Transfer target focused.'
            );
            return { ...result, payload: result.ok ? { targetId } : undefined };
        },
        async transferShortlist(targetId) {
            return executeCommand(
                () => runtime.managerTransfersAction('action', { transferShortlist: targetId }),
                'Transfer target shortlisted.'
            );
        },
        async transferExpressInterest(targetId) {
            return executeCommand(
                () => runtime.managerTransfersAction('action', { transferExpressInterest: targetId }),
                'Transfer interest expressed.'
            );
        },
        async transferListPlayer(playerId, targetPrice) {
            return executeCommand(
                () => runtime.managerTransfersAction('action', {
                    transferListPlayer: String(playerId),
                    targetPrice: String(targetPrice)
                }),
                'Player listed on transfer market.'
            );
        },
        async transferUnlistPlayer(playerId) {
            return executeCommand(
                () => runtime.managerTransfersAction('action', { transferUnlistPlayer: String(playerId) }),
                'Player removed from transfer market.'
            );
        },
        async introSetFormation(formation) {
            return executeCommand(
                () => runtime.managerIntroAction('set-formation', { formation }),
                'Intro formation updated.'
            );
        },
        async introSwapSlots(source, target) {
            return executeCommand(
                () => runtime.managerIntroAction('swap-slots', {
                    sourceGroup: source.group,
                    sourceIndex: String(source.index),
                    targetGroup: target.group,
                    targetIndex: String(target.index)
                }),
                'Intro selection slots swapped.'
            );
        },
        async introBack() {
            return executeCommand(() => runtime.managerIntroBack(), 'Exited intro view.');
        },
        async introAuto() {
            return executeCommand(() => runtime.managerIntroAuto(), 'Auto selection applied.');
        },
        async introStart() {
            return executeCommand(() => runtime.managerIntroStart(), 'Started managed match.');
        },
        async introSkip() {
            return executeCommand(() => runtime.managerIntroSkip(), 'Managed match skipped to result.');
        },
        async resultContinue() {
            return executeCommand(() => runtime.managerResultContinue(), 'Result screen dismissed.');
        },
        async daySummaryBack() {
            return executeCommand(() => runtime.managerDaySummaryBack(), 'Day summary closed.');
        },
        async daySummaryContinue() {
            return executeCommand(() => runtime.managerDaySummaryContinue(), 'Day summary continued.');
        },
        async backToHub() {
            return executeCommand(() => runtime.managerBackToHub(), 'Returned to manager hub.');
        }
    };
}

export function createUiBridgeV2(
    runtime: AppRuntime,
    getPathname: () => string,
    navigate: (path: string) => void
): UiBridgeV2 {
    let latestSnapshot = mapUiSnapshotV2(
        {
            mode: 'splash',
            splash: {
                teamName: 'Managed United',
                hint: '',
                busy: false,
                canLoadCampaign: false
            },
            manager: null
        },
        getPathname()
    );

    runtime.subscribe((snapshot) => {
        latestSnapshot = mapUiSnapshotV2(snapshot, getPathname());
    });

    return {
        getSnapshot() {
            return latestSnapshot;
        },
        subscribe(listener) {
            listener(latestSnapshot);
            return runtime.subscribe((snapshot) => {
                latestSnapshot = mapUiSnapshotV2(snapshot, getPathname());
                listener(latestSnapshot);
            });
        },
        commands: createUiCommandBusV2(runtime, navigate)
    };
}

declare global {
    interface Window {
        __TSM_MANAGER_UI_V2__?: UiBridgeV2;
        __TSM_MANAGER_UI_V2_SNAPSHOT__?: UiSnapshotV2;
    }
}
